import { useIsMounted } from '@ps/web-shared-components';
import { useEffect, useState } from 'react';

enum SelectionDirection {
  None = 'none',
  Forward = 'forward',
  Backward = 'backward'
}

interface SelectionProp {
  start: number;
  end: number;
  direction: SelectionDirection;
}

const useCursorPosition = (
  inputEl: React.RefObject<(HTMLInputElement & HTMLTextAreaElement) | null>,
  groupLength: number,
  fieldName: string
) => {
  const [handleCursorPosition, setHandleCursorPosition] = useState(false);
  const isMounted = useIsMounted();
  const [selection, setSelection] = useState<SelectionProp>({
    start: 0,
    end: 0,
    direction: SelectionDirection.None
  });

  useEffect(() => {
    const { current } = inputEl;
    if (!current) {
      return;
    }
    const handleKeyDown = async (event: KeyboardEvent) => {
      if (isMounted() && (event.target as HTMLElement).id === fieldName) {
        if (!(event.target as HTMLInputElement).value) {
          setHandleCursorPosition(false);
          setSelection({
            start: 0,
            end: 0,
            direction: SelectionDirection.None
          });
        }
        const isNumberKey = isFinite(+event.key);

        if (isNumberKey && handleCursorPosition) {
          setSelection({
            start: current.selectionStart,
            end: current.selectionEnd,
            direction: SelectionDirection.Forward
          });
        } else if (event.key === 'Backspace' || event.key === 'Delete') {
          setHandleCursorPosition(true);
          setSelection({
            start: current.selectionStart - 1,
            end: current.selectionEnd - 1,
            direction: SelectionDirection.Backward
          });
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [fieldName, handleCursorPosition, inputEl, isMounted, selection.start]);

  const setCursorPosition = () => {
    if (!inputEl.current) {
      return;
    }
    const { current } = inputEl;

    if (selection.direction === SelectionDirection.Backward) {
      current.setSelectionRange(selection.start, selection.end, SelectionDirection.Backward);
    } else if (handleCursorPosition) {
      let offset = 4;
      const valueLength = current.value.substring(0, selection.start).length;
      const isBlock = inputEl.current.value
        .substring(0, selection.start)
        .replace(/\D+/g, '').length;

      if (isBlock % groupLength === 0 && selection.start % groupLength === 0) {
        offset = groupLength;
      }
      current.setSelectionRange(
        valueLength + offset,
        valueLength + offset,
        SelectionDirection.Forward
      );
    } else {
      current.setSelectionRange(
        current.value.length,
        current.value.length,
        SelectionDirection.None
      );
    }
  };
  return setCursorPosition;
};

export default useCursorPosition;
